/**
 * Custom Amplify Storage
 *
 * This storage implementation stores User Pool tokens in cookies (for cross-domain sharing)
 * while keeping Identity Pool credentials in localStorage (to avoid cookie size limitations).
 */

// Enable debug logging (set to false in production)
const DEBUG = process.env.NODE_ENV === 'development';

// Keys that must stay in localStorage (not cookies)
// Identity Pool credentials, federation info, and other AWS-specific data
// should not be shared via cookies to avoid corruption and size limits
const LOCALSTORAGE_KEYS = [
  'aws.cognito.identity-id',
  'aws.cognito.identity-providers',
  'aws-cognito-origin',
  'CognitoIdentityId-',
  'aws-amplify-federatedInfo',
  'aws-amplify-analytics',
  'aws.cognito.identity-credentials',
];

// Only these specific token types should be in cookies for cross-domain sharing
const COOKIE_TOKEN_TYPES = ['idToken', 'accessToken', 'refreshToken', 'clockDrift', 'LastAuthUser'];

class CustomAmplifyStorage {
  /**
   * Constructor
   * @param {Object} cookieConfig - The configuration for the cookies
   */
  constructor(cookieConfig) {
    this.cookieConfig = cookieConfig;
    if (DEBUG) {
      console.log('[CustomAmplifyStorage] Initialized with config:', cookieConfig);
    }
  }

  /**
   * Determine if a key should be stored in cookies or localStorage
   * @param {string} key - The key to check
   * @returns {boolean} True if the key should be stored in cookies, false otherwise
   */
  shouldUseCookie(key) {
    // First check if it should explicitly stay in localStorage
    if (LOCALSTORAGE_KEYS.some((prefix) => key.includes(prefix))) {
      if (DEBUG) console.log(`[Storage] Key "${key}" -> localStorage (identity/credentials)`);
      return false;
    }

    // Only put specific User Pool tokens in cookies
    if (key.includes('CognitoIdentityServiceProvider')) {
      // Check if it's one of the allowed token types
      const useCookie = COOKIE_TOKEN_TYPES.some((tokenType) => key.includes(`.${tokenType}`));
      return useCookie;
    }

    // Everything else stays in localStorage by default for safety
    if (DEBUG) console.log(`[Storage] Key "${key}" -> localStorage (default)`);
    return false;
  }

  /**
   * Set item in storage
   * @param {string} key - The key of the item to set
   * @param {string} value - The value of the item to set
   * @returns {string} The value of the item
   */
  setItem(key, value) {
    try {
      if (this.shouldUseCookie(key)) {
        // Check if value is too large for cookies (> 4KB)
        if (value && value.length > 4000) {
          console.warn(
            `Value for key "${key}" is too large for cookies (${value.length} bytes), storing in localStorage instead`
          );
          localStorage.setItem(key, value);
        } else {
          this.setCookie(key, value);
        }
      } else {
        localStorage.setItem(key, value);
      }
      return value;
    } catch (error) {
      console.error(`Error setting item "${key}":`, error);
      // Fallback to localStorage on error
      localStorage.setItem(key, value);
      return value;
    }
  }

  /**
   * Get item from storage
   * @param {string} key - The key of the item to get
   * @returns {string|null} The value of the item
   */
  getItem(key) {
    if (this.shouldUseCookie(key)) {
      // Check cookie first, fallback to localStorage for backwards compatibility
      const cookieValue = this.getCookie(key);
      if (cookieValue !== null) {
        return cookieValue;
      }
      // Check localStorage as fallback (in case data was set before custom storage)
      const localStorageValue = localStorage.getItem(key);
      if (localStorageValue !== null) {
        // Migrate to cookie
        this.setCookie(key, localStorageValue);
        localStorage.removeItem(key);
        return localStorageValue;
      }
      return null;
    } else {
      return localStorage.getItem(key);
    }
  }

  /**
   * Remove item from storage
   * @param {string} key - The key of the item to remove
   * @returns {void}
   */
  removeItem(key) {
    if (this.shouldUseCookie(key)) {
      this.deleteCookie(key);
    } else {
      localStorage.removeItem(key);
    }
  }

  /**
   * Clear all storage (both cookies and localStorage)
   * @returns {void}
   */
  clear() {
    // Clear all Cognito-related cookies
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name.includes('CognitoIdentityServiceProvider')) {
        this.deleteCookie(name);
      }
    }

    // Clear all Cognito and AWS-related items from localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.includes('CognitoIdentityServiceProvider') ||
          key.includes('aws.cognito') ||
          key.includes('aws-amplify') ||
          key.includes('aws-cognito'))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  /**
   * Set a cookie
   * @param {string} name - The name of the cookie to set
   * @param {string} value - The value of the cookie to set
   */
  setCookie(name, value) {
    const { domain, path, expires, secure, sameSite } = this.cookieConfig;
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + expires);

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    cookieString += `; expires=${expirationDate.toUTCString()}`;
    cookieString += `; path=${path}`;

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += '; secure';
    }

    if (sameSite) {
      cookieString += `; SameSite=${sameSite}`;
    }

    document.cookie = cookieString;
  }

  /**
   * Get a cookie
   * @param {string} name - The name of the cookie to get
   * @returns {string|null} The value of the cookie
   */
  getCookie(name) {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      let c = cookie.trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }

    return null;
  }

  /**
   * Delete a cookie
   * @param {string} name - The name of the cookie to delete
   */
  deleteCookie(name) {
    const { domain, path } = this.cookieConfig;
    let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    document.cookie = cookieString;
  }
}

export default CustomAmplifyStorage;
